import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

interface MemberProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  badge_number?: string;
  station?: string;
  org_name?: string;
  is_active: boolean;
}

interface GearItem {
  id: string;
  item_type_id: string;
  item_type_name: string;
  service_life_years?: number;
  track_gear_classification?: boolean;
  serial_number?: string;
  manufacturer?: string;
  model?: string;
  size?: string;
  date_manufactured?: string;
  condition: string;
  gear_classification?: string | null;
  station?: string;
  notes?: string;
}

interface GearItemType {
  id: string;
  name: string;
  service_life_years?: number;
  track_gear_classification: boolean;
  required_frontline_qty: number;
  required_backup_qty: number;
}

interface GearCardData {
  member: MemberProfile;
  items: GearItem[];
  itemTypes: GearItemType[];
}

function daysUntilExpiry(dateManufactured: string | undefined, serviceLifeYears: number | undefined): number | null {
  if (!dateManufactured || !serviceLifeYears) return null;
  const mfg = new Date(dateManufactured);
  const expiry = new Date(mfg);
  expiry.setFullYear(expiry.getFullYear() + serviceLifeYears);
  const now = new Date();
  return Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function formatDate(d: string | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function expiryDate(dateManufactured: string | undefined, serviceLifeYears: number | undefined): string {
  if (!dateManufactured || !serviceLifeYears) return '—';
  const mfg = new Date(dateManufactured);
  mfg.setFullYear(mfg.getFullYear() + serviceLifeYears);
  return formatDate(mfg.toISOString());
}

const conditionColors: Record<string, { bg: string; color: string }> = {
  good: { bg: '#f0fdf4', color: '#166534' },
  fair: { bg: '#fffbeb', color: '#92400e' },
  poor: { bg: '#fef2f2', color: '#b91c1c' },
  out_of_service: { bg: '#f3f4f6', color: '#6b7280' },
};

export default function MemberGear() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<GearCardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const targetId = id || 'me';

  useEffect(() => {
    api.get<GearCardData>(`/members/${targetId}/gear`)
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [targetId]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading gear card...</div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#b91c1c' }}>{error}</div>;
  if (!data) return null;

  const { member, items, itemTypes } = data;
  const canManage = user?.role === 'admin' || user?.role === 'quartermaster';

  // Group items by item type
  const grouped: Record<string, GearItem[]> = {};
  for (const item of items) {
    if (!grouped[item.item_type_id]) grouped[item.item_type_id] = [];
    grouped[item.item_type_id].push(item);
  }

  // Build gap analysis
  const typeMap = new Map(itemTypes.map(t => [t.id, t]));

  interface GapInfo {
    typeName: string;
    typeId: string;
    requiredFrontline: number;
    requiredBackup: number;
    hasFrontline: number;
    hasBackup: number;
    missingFrontline: number;
    missingBackup: number;
  }

  const gaps: GapInfo[] = [];
  for (const type of itemTypes) {
    if (type.required_frontline_qty === 0 && type.required_backup_qty === 0) continue;
    const typeItems = grouped[type.id] || [];
    const frontline = typeItems.filter(i => i.gear_classification === 'frontline').length;
    const backup = typeItems.filter(i => i.gear_classification === 'backup').length;
    const missingFl = Math.max(0, type.required_frontline_qty - frontline);
    const missingBk = Math.max(0, type.required_backup_qty - backup);
    if (missingFl > 0 || missingBk > 0 || type.required_frontline_qty > 0 || type.required_backup_qty > 0) {
      gaps.push({
        typeName: type.name,
        typeId: type.id,
        requiredFrontline: type.required_frontline_qty,
        requiredBackup: type.required_backup_qty,
        hasFrontline: frontline,
        hasBackup: backup,
        missingFrontline: missingFl,
        missingBackup: missingBk,
      });
    }
  }

  const hasMissingGear = gaps.some(g => g.missingFrontline > 0 || g.missingBackup > 0);

  const cardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: 24,
  };

  return (
    <div>
      {/* Back link */}
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: 14, padding: 0, fontWeight: 500 }}>
          &larr; Back
        </button>
      </div>

      {/* Member header */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px', color: '#111827' }}>
              {member.first_name} {member.last_name}
            </h1>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 14, color: '#6b7280' }}>
              {member.badge_number && <span>Badge: {member.badge_number}</span>}
              {member.station && <span>Station: {member.station}</span>}
              <span>{member.email}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{
              padding: '4px 10px', borderRadius: 6, fontSize: 13, fontWeight: 600,
              background: member.role === 'admin' ? '#fef2f2' : member.role === 'quartermaster' ? '#eff6ff' : '#f0fdf4',
              color: member.role === 'admin' ? '#b91c1c' : member.role === 'quartermaster' ? '#1e40af' : '#166534',
            }}>
              {member.role === 'admin' ? 'Admin' : member.role === 'quartermaster' ? 'QM' : 'User'}
            </span>
            <span style={{
              padding: '4px 10px', borderRadius: 6, fontSize: 13, fontWeight: 500,
              background: member.is_active ? '#f0fdf4' : '#fef2f2',
              color: member.is_active ? '#166534' : '#b91c1c',
            }}>
              {member.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Quick stats */}
        <div style={{ display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>{items.length}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Active Items</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {items.filter(i => i.gear_classification === 'frontline').length}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Frontline</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {items.filter(i => i.gear_classification === 'backup').length}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Backup</div>
          </div>
          {hasMissingGear && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#b91c1c' }}>
                {gaps.reduce((sum, g) => sum + g.missingFrontline + g.missingBackup, 0)}
              </div>
              <div style={{ fontSize: 12, color: '#b91c1c' }}>Missing</div>
            </div>
          )}
        </div>
      </div>

      {/* Gap Detection */}
      {gaps.length > 0 && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 16px', color: '#374151' }}>
            Equipment Coverage
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 600 }}>Item Type</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px', color: '#6b7280', fontWeight: 600 }}>Frontline</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px', color: '#6b7280', fontWeight: 600 }}>Backup</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px', color: '#6b7280', fontWeight: 600 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {gaps.map(g => {
                  const isComplete = g.missingFrontline === 0 && g.missingBackup === 0;
                  return (
                    <tr key={g.typeId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{g.typeName}</td>
                      <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                        <span style={{
                          color: g.missingFrontline > 0 ? '#b91c1c' : '#166534',
                          fontWeight: g.missingFrontline > 0 ? 700 : 400,
                        }}>
                          {g.hasFrontline}/{g.requiredFrontline}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                        <span style={{
                          color: g.missingBackup > 0 ? '#b91c1c' : '#166534',
                          fontWeight: g.missingBackup > 0 ? 700 : 400,
                        }}>
                          {g.hasBackup}/{g.requiredBackup}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                          background: isComplete ? '#f0fdf4' : '#fef2f2',
                          color: isComplete ? '#166534' : '#b91c1c',
                        }}>
                          {isComplete ? 'Complete' : 'MISSING'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Gear by Type */}
      {Object.keys(grouped).length === 0 ? (
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>
            No active gear assigned to this member.
          </div>
        </div>
      ) : (
        Object.entries(grouped).map(([typeId, typeItems]) => {
          const typeInfo = typeMap.get(typeId);
          return (
            <div key={typeId} style={cardStyle}>
              <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 16px', color: '#374151' }}>
                {typeInfo?.name || 'Unknown Type'}
                <span style={{ fontSize: 13, fontWeight: 400, color: '#9ca3af', marginLeft: 8 }}>
                  ({typeItems.length} item{typeItems.length !== 1 ? 's' : ''})
                </span>
              </h2>
              <div style={{ display: 'grid', gap: 12 }}>
                {typeItems.map(item => {
                  const days = daysUntilExpiry(item.date_manufactured, item.service_life_years);
                  const isExpired = days !== null && days < 0;
                  const isExpiringSoon = days !== null && days >= 0 && days <= 365;
                  const cc = conditionColors[item.condition] || conditionColors.good;

                  return (
                    <Link to={`/inventory/${item.id}`} key={item.id}
                      style={{
                        display: 'block', textDecoration: 'none', color: 'inherit',
                        padding: 16, borderRadius: 8,
                        border: `1px solid ${isExpired ? '#fca5a5' : isExpiringSoon ? '#fcd34d' : '#e5e7eb'}`,
                        background: isExpired ? '#fef2f2' : isExpiringSoon ? '#fffbeb' : '#fafafa',
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            {item.gear_classification && (
                              <span style={{
                                padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                                background: item.gear_classification === 'frontline' ? '#dbeafe' : '#f3e8ff',
                                color: item.gear_classification === 'frontline' ? '#1e40af' : '#7c3aed',
                              }}>
                                {item.gear_classification}
                              </span>
                            )}
                            <span style={{
                              padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                              background: cc.bg, color: cc.color,
                            }}>
                              {item.condition.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div style={{ fontWeight: 600, fontSize: 15 }}>
                            {[item.manufacturer, item.model].filter(Boolean).join(' ') || 'No make/model'}
                          </div>
                          {item.serial_number && (
                            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                              S/N: {item.serial_number}
                            </div>
                          )}
                          {item.size && (
                            <div style={{ fontSize: 13, color: '#6b7280' }}>
                              Size: {item.size}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', fontSize: 13 }}>
                          <div style={{ color: '#6b7280' }}>
                            Mfg: {formatDate(item.date_manufactured)}
                          </div>
                          <div style={{ color: '#6b7280' }}>
                            Exp: {expiryDate(item.date_manufactured, item.service_life_years)}
                          </div>
                          {days !== null && (
                            <div style={{
                              fontWeight: 600, marginTop: 4,
                              color: isExpired ? '#b91c1c' : isExpiringSoon ? '#92400e' : '#166534',
                            }}>
                              {isExpired
                                ? `Expired ${Math.abs(days)} days ago`
                                : `${days} days remaining`}
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
